/**
 * VigilanceContrats — Dashboard des alertes préavis + échéances orphelines.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { AlertTriangle, Clock, EyeOff, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const fmtEUR = (v) => new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—";

export default function VigilanceContrats({ embedded = false } = {}) {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("contracts.write");
  const [data, setData] = useState({ preavis_urgent: [], echeance_orpheline: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/vigilance-contracts");
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const ignore = async (cid, kind) => {
    if (!window.confirm("Ignorer cette alerte ?")) return;
    try {
      await api.post(`/vigilance-contracts/${cid}/ignore?kind=${kind}`);
      toast.success("Alerte masquée.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="vigilance-contracts-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          <ShieldAlert className="h-7 w-7 text-amber-600" /> Vigilance Contrats
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Alertes hebdomadaires : préavis à envoyer et échéances non traitées.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-red-600">Préavis urgents</div>
          <div className="mt-1 text-2xl font-semibold font-mono">{data.preavis_urgent.length}</div>
          <div className="text-[10px] text-slate-400 mt-1">Deadline ≤ 30 jours, non envoyé</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-amber-600">Échéances orphelines</div>
          <div className="mt-1 text-2xl font-semibold font-mono">{data.echeance_orpheline.length}</div>
          <div className="text-[10px] text-slate-400 mt-1">Dépassées &gt; 15j sans reconduction ni résiliation</div>
        </CardContent></Card>
      </div>

      <Card>
        <div className="p-4 border-b bg-red-50/50">
          <h2 className="text-sm font-semibold text-red-900 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Préavis à envoyer
          </h2>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-400">Chargement…</div>
        ) : data.preavis_urgent.length === 0 ? (
          <div className="p-8 text-center text-sm text-emerald-600" data-testid="empty-preavis">
            ✓ Aucun préavis urgent
          </div>
        ) : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Contrat</TableHead>
              <TableHead>Tiers</TableHead>
              <TableHead>Deadline</TableHead>
              <TableHead>Jours</TableHead>
              <TableHead className="text-right">MRR</TableHead>
              {canWrite && <TableHead />}
            </TableRow></TableHeader>
            <TableBody>
              {data.preavis_urgent.map((a) => (
                <TableRow key={a.contract_id} data-testid={`preavis-row-${a.contract_id}`}>
                  <TableCell>
                    <Link to={`/app/contracts/${a.contract_id}`}
                           className="font-medium hover:underline">
                      {a.title}
                    </Link>
                  </TableCell>
                  <TableCell className="text-xs">{a.party_name}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtDate(a.date_limite_preavis)}</TableCell>
                  <TableCell>
                    <Badge className={a.severity === "critical"
                      ? "bg-red-100 text-red-700 border-red-200"
                      : "bg-amber-100 text-amber-700 border-amber-200"}>
                      {a.days_until_deadline < 0
                        ? `Dépassé J+${Math.abs(a.days_until_deadline)}`
                        : `J-${a.days_until_deadline}`}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono">{fmtEUR(a.amount_ht)}</TableCell>
                  {canWrite && (
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm"
                               onClick={() => ignore(a.contract_id, "preavis_urgent")}
                               data-testid={`ignore-preavis-${a.contract_id}`}>
                        <EyeOff className="h-3.5 w-3.5 mr-1" /> Ignorer
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Card>
        <div className="p-4 border-b bg-amber-50/50">
          <h2 className="text-sm font-semibold text-amber-900 flex items-center gap-2">
            <Clock className="h-4 w-4" /> Échéances orphelines
          </h2>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-400">Chargement…</div>
        ) : data.echeance_orpheline.length === 0 ? (
          <div className="p-8 text-center text-sm text-emerald-600" data-testid="empty-orpheline">
            ✓ Aucune échéance orpheline
          </div>
        ) : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Contrat</TableHead>
              <TableHead>Tiers</TableHead>
              <TableHead>Fin</TableHead>
              <TableHead>Depuis</TableHead>
              <TableHead className="text-right">MRR</TableHead>
              {canWrite && <TableHead />}
            </TableRow></TableHeader>
            <TableBody>
              {data.echeance_orpheline.map((a) => (
                <TableRow key={a.contract_id} data-testid={`orpheline-row-${a.contract_id}`}>
                  <TableCell>
                    <Link to={`/app/contracts/${a.contract_id}`}
                           className="font-medium hover:underline">
                      {a.title}
                    </Link>
                  </TableCell>
                  <TableCell className="text-xs">{a.party_name}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtDate(a.end_date)}</TableCell>
                  <TableCell>
                    <Badge className="bg-red-100 text-red-700 border-red-200">
                      J+{a.days_since_expiry}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono">{fmtEUR(a.amount_ht)}</TableCell>
                  {canWrite && (
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm"
                               onClick={() => ignore(a.contract_id, "echeance_orpheline")}
                               data-testid={`ignore-orpheline-${a.contract_id}`}>
                        <EyeOff className="h-3.5 w-3.5 mr-1" /> Ignorer
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
